import { z } from "zod";

import type { SectionKey } from "../types/gigs.types";

import { isValidURL } from "./gigsClaimUtils";

const baseSchema = z.object({
  // Core fields — always required
  country: z.string().min(1, "Please choose a country"),
  venueName: z.string().min(1, "Please add a venue name"),
  performer: z.string().min(1, "Please add a performer"),
  city: z.string().min(1, "Please add a city"),
  address: z.string().min(1, "Add an address"),
  postcode: z.string().min(1, "Add a postcode"),
  time: z.string().min(1, "Please select a time"),
  date: z.string().min(1, "Please add a date").refine(d => !Number.isNaN(new Date(d).getTime()), "Invalid Date"),
  setlist: z.string({ required_error: "Error with setlist claim" }).optional(),

  venueId: z.union([z.string(), z.number()]).optional(),
  venuePremisesCategoryId: z.number().optional(),
  venuePremisesTypeId: z.number().optional(),
  prsId: z.union([z.string(), z.number()]).optional(),
  venueIsApproved: z.boolean().optional(),

  admissionEUR: z.number().optional(),
  admissionCAD: z.number().optional(),
  admissionDKK: z.number().optional(),
  admissionFee: z.string().optional(),
  audienceNumber: z.number().optional(),
  venueCapacity: z.string().optional(),

  // Gig extras
  eventName: z.string().optional(),
  eventWebsiteURL: z.string().optional(),
  otherActs: z.string().optional(),
  evidenceURL: z.string().optional(),
  proofOfPerformance: z.string().optional(),
  lineUpPosition: z.string().optional(),

  promotor: z.string().optional(),

  promotorName: z.string().optional(),
  promotorAddress1: z.string().optional(),
  promotorStreet: z.string().optional(),
  promotorCity: z.string().optional(),
  promotorPostcode: z.string().optional(),
  promotorCountry: z.string().optional(),
  promotorPhone: z.string().optional(),
  promotorEmail: z.string().optional(),

  festivalStageName: z.string().optional(),
  festivalPosition: z.string().optional(),

  headlineArtist: z.string().optional(),
  supportArtist1: z.string().optional(),
  supportArtist2: z.string().optional(),
  supportArtist3: z.string().optional(),
});

export type GigClaimFormValues = z.infer<typeof baseSchema>;

const SKIP_SECTIONS = new Set<SectionKey>(["promotorInfo", "lineUpPositionAlt", "evidence"]);

export function createGigsClaimSchema(
  sections: SectionKey[],
  festival: boolean,
  otherPerformers: boolean,
  underTwelveEuros: boolean,
  isUpdate: boolean = false,
) {
  return baseSchema.superRefine((data, ctx) => {
    if (festival) {
      if (data.festivalStageName == null || data.festivalStageName === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["festivalStageName"],
          message: "Incomplete",
        });
      }
      if (data.festivalPosition == null || data.festivalPosition === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["festivalPosition"],
          message: "Incomplete",
        });
      }
    }

    if (otherPerformers) {
      if (data.headlineArtist == null || data.headlineArtist === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["headlineArtist"],
          message: "Incomplete",
        });
      }
      if (data.country === "FR" && (data.supportArtist1 == null || data.supportArtist1 === "")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["supportArtist1"],
          message: "Incomplete",
        });
      }
    }
    if (!isUpdate && (data.setlist == null || data.setlist === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["setlist"],
        message: "Please select a setlist",
      });
    }

    if (data.country === "CA") {
      if (data.admissionCAD == null || data.admissionCAD < 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["admissionCAD"],
          message: "Over 10 CAD Error",
        });
      }
    }

    if (underTwelveEuros) {
      if (data.admissionEUR == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["admissionEUR"],
          message: "Incomplete",
        });
      }

      if (data.evidenceURL == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["evidenceURL"],
          message: "Incomplete",
        });
      }

      if (data.eventWebsiteURL == null || data.eventWebsiteURL === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["eventWebsiteURL"],
          message: "Incomplete",
        });
      }
      else if (!isValidURL(data.eventWebsiteURL)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["eventWebsiteURL"],
          message: "Invalid Url",
        });
      }
    }

    for (const section of sections) {
      if (SKIP_SECTIONS.has(section))
        continue;
      const value = (data as Record<string, unknown>)[section];
      if (value == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [section],
          message: "Incomplete",
        });
      }
    }

    if (sections.includes("promotorInfo")) {
      const promotorFields = [
        "promotorName",
        "promotorAddress1",
        "promotorStreet",
        "promotorCity",
        "promotorPostcode",
        "promotorCountry",
        "promotorPhone",
        "promotorEmail",
      ] as const;

      for (const field of promotorFields) {
        if (data[field] == null || data[field] === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: "Incomplete",
          });
        }
      }
    }
  });
}
